<template>
	<div>
		<div class="tag-sections">
			<section v-if="organisationAreaEligible" class="tag">
				<h2>
					Aufgrund deiner Auswahl {{ organisationAreaTag }}:<br/>
					In welchem Organisationsbereich möchtest du tätig sein?
				</h2>
				<OrganisationalArea
					v-model="organisationalArea"
					button-alignment="left"
					class="mt-3"
					:functions="functions"
					@input="emitChangeEvent"
				/>
			</section>
			<section v-if="industrialSectorEligible" class="tag">
				<h2>{{ industrialSectorTitle }}</h2>
				<div v-if="!industrialSectorsMandatory" class="mt-2">
					<RadioGroup
						v-model="industrialSectorsIrrelevant"
						:options="industrialSectorsIrrelevantOptions"
						@input="industrialSectorsIrrelevantChanged"
					/>
				</div>
				<IndustrialSectors
					v-if="industrialSectorsIrrelevant === 'no' || industrialSectorsMandatory"
					v-model="industrialSectors"
					ref="industrialSectors"
					button-alignment="left"
					class="mt-3"
					:nothing-allowed="!industrialSectorsMandatory"
					@input="industrialSectorsChanged"
				/>
				<div v-if="!industrialSectorsMandatory && industrialSectorsIrrelevant === 'yes'" class="mt-2">
					Sei offen für alle Branchen. Du kannst deine Eingaben später noch in deinem Profil anpassen.
				</div>
			</section>
		</div>
	</div>
</template>

<script>
import IndustrialSectors from "@/vue/application/IndustrialSectors.vue";
import OrganisationalArea from "@/vue/application/OrganisationalArea.vue";
import RadioGroup from "@/vue/common/form/RadioGroup.vue";
import {isDefined} from "@/helper/Value";
import {arrayIsNotEmpty, isEmptyArray} from "@/helper/Array";
import Button from "@/vue/common/Button.vue";
import {isMandatory} from "@/helper/characteristic/IndustrialSector";
import {clearSelected, isEligible} from "@/helper/characteristic/OrganisationalArea";
import * as Tag from "@/helper/characteristic/Tag";
import * as LabelType from "@/helper/characteristic/Label";
import _ from "lodash";
import {mapState} from "vuex";

const IRRELEVANT_YES = 'yes';
const IRRELEVANT_NO = 'no';

export default {
	components: {
		Button,
		RadioGroup,
		IndustrialSectors,
		OrganisationalArea
	},

	props: {
		value: {
			type: Object,
			required: false,
		},
		title: {
			type: String,
			required: true,
		},
		functions: {
			type: Array,
			required: true,
		},
		suppressIndustrialSector: {
			type: Boolean,
			required: true
		},
		organisationalAreaEligible: {
			type: Boolean,
			required: true
		},
	},

	mounted ()
	{
		if (!isDefined(this.industrialSectors) && !this.industrialSectorsMandatory)
		{
			this.industrialSectorsIrrelevant = IRRELEVANT_YES;
		}

		if (isDefined(this.organisationalArea))
		{
			this.organisationalArea = clearSelected(this.organisationalArea, this.meta, this.functions);
		}

		this.emitChangeEvent();
	},

	data ()
	{
		return {
			industrialSectorsIrrelevant: isDefined(this.value?.industrialSectors)
				? (isEmptyArray(this.value.industrialSectors) ? IRRELEVANT_YES : IRRELEVANT_NO)
				: undefined,
			industrialSectors: this.value?.industrialSectors,
			organisationalArea: this.value?.organisationalArea,
		};
	},

	computed: {
		...mapState('meta', ['meta']),
		industrialSectorEligible ()
		{
			return !this.suppressIndustrialSector;
		},
		industrialSectorsMandatory ()
		{
			return isMandatory(
				this.functions.map(item => item.item)
			);
		},
		industrialSectorTitle ()
		{
			if (this.industrialSectorsMandatory)
			{
				return 'Deine Tätigkeit korreliert stark zu Branche, bitte wähle daher aus ' +
					'in welcher Branche/Dienstleistung/Produkte du willst.';
			}

			return 'Soll dein zukünftiger Arbeitgeber etwas Bestimmtes machen, also ein bestimmtes Tätigkeitsfeld haben, in dem er tätig ist?';
		},
		industrialSectorsIrrelevantOptions ()
		{
			return [
				{value: IRRELEVANT_NO, text: 'Ja'},
				{value: IRRELEVANT_YES, text: 'Egal'},
			];
		},
		industrialSectorsValid ()
		{
			if (!this.industrialSectorEligible)
			{
				return true;
			}

			if (this.industrialSectorsMandatory || this.industrialSectorsIrrelevant === IRRELEVANT_NO)
			{
				return arrayIsNotEmpty(this.industrialSectors);
			}

			return isDefined(this.industrialSectorsIrrelevant);
		},
		organisationAreaTag ()
		{
			let relevantCharacteristics = this.functions
				.filter(
					item => Tag.hasTag(item.item, Tag.FUNCTION_ORGANISATIONAL_AREA_ELIGIBLE)
				)
				.map(item => item.item);

			if (relevantCharacteristics.length === 0)
			{
				return '';
			}

			relevantCharacteristics = _.orderBy(relevantCharacteristics, ['label'], ['asc']);

			let text = LabelType.get(
				_.first(relevantCharacteristics),
				LabelType.APPLICATION
			);

			if (relevantCharacteristics.length > 1)
			{
				text += ', ...';
			}

			return text;
		},
		organisationAreaEligible ()
		{
			return isEligible(
				this.functions.map(item => item.item)
			);
		},
		organisationAreaValid ()
		{
			return !this.organisationAreaEligible || isDefined(this.organisationalArea);
		},
		valid ()
		{
			return this.industrialSectorsValid
				&& this.organisationAreaValid;
		},
	},

	methods: {
		industrialSectorsIrrelevantChanged (value)
		{
			if (value === IRRELEVANT_NO)
			{
				this.industrialSectors = undefined;

				this.$nextTick(() =>
				{
					this.$refs.industrialSectors.edit();
				});
			}
			else
			{
				this.industrialSectors = [];
			}

			this.emitChangeEvent();
		},
		industrialSectorsChanged ()
		{
			if (this.industrialSectors === undefined)
			{
				this.industrialSectorsIrrelevant = undefined;
			}

			if (this.industrialSectors && this.industrialSectors.length === 0)
			{
				this.industrialSectorsIrrelevant = IRRELEVANT_YES;
			}

			this.emitChangeEvent();
		},
		emitChangeEvent ()
		{
			this.$emit('input', {
				industrialSectors: (
					(!this.industrialSectorsMandatory && this.industrialSectorsIrrelevant === IRRELEVANT_YES)
					|| !this.industrialSectorEligible
				)
					? []
					: this.industrialSectors,
				organisationalArea: this.organisationAreaEligible
					? this.organisationalArea
					: undefined,
				valid: this.valid,
			});
		}
	}
}
</script>
